import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import { ProductsContainer, BreadcrumbsWrapper }
  from '@fuww/library/src/CompanyProfile';
import { Breadcrumbs } from '@fuww/library/src/Breadcrumb';
import Head from '../Head';
import withQuery from '../../lib/withQuery';
import profileBySlugQuery
  from '../../lib/queries/products/profileBySlugQuery.graphql';
import CompanyProductsWrapper
  from './CompanyProfilePages/Products';
import DynamicProducts from '../Products/DynamicProducts';
import messages from '../../lib/messages.json';
import HomeLink from '../HomeLink';
import LinkAndAnchor from '../LinkAndAnchor';
import {
  companyProfileCollectionImageConfigurations,
  companyProfileImageConfigurations,
  companyProfileLogoImageConfigurations,
  companyProfileMobileImageConfigurations,
} from '../../lib/imageConfigurations';

interface MarketConfiguration {
  marketId: number;
  breadcrumbRoute: string;
  titleMessageKey: string;
  productRoute: string;
}

const configurationPropertyType = PropTypes.shape({
  marketId: PropTypes.number.isRequired,
  breadcrumbRoute: PropTypes.string.isRequired,
  titleMessageKey: PropTypes.string.isRequired,
  productRoute: PropTypes.string.isRequired,
}).isRequired;
const companyMarketProductsPropertyTypes = {
  data: PropTypes.shape({
    profileBySlug: PropTypes.shape({
      id: PropTypes.string.isRequired,
      brands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
      }).isRequired).isRequired,
      page: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  configuration: configurationPropertyType,
};

type CompanyMarketProductsProperties = InferProps<
  typeof companyMarketProductsPropertyTypes
>;

const CompanyMarketProducts = ({
  data: { profileBySlug },
  configuration,
}: CompanyMarketProductsProperties) => {
  const intl = useIntl();

  const { id: profileId, brands, page: { slug, title } } = profileBySlug;
  const {
    marketId, breadcrumbRoute, titleMessageKey, productRoute,
  } = configuration as MarketConfiguration;

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="company-directory"
      passHref
    >
      {intl.formatMessage(messages.companies)}
    </LinkAndAnchor>,
    <LinkAndAnchor
      route={breadcrumbRoute}
      passHref
      params={{ slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={intl.formatMessage(
          messages[titleMessageKey],
          { title },
        )}
        description={
          intl.formatMessage(messages['company-profile.description'], { title })
        }
        subjectType="Profile"
        subjectId={profileId}
        payload={{ marketId }}
      />
      <BreadcrumbsWrapper>
        <Breadcrumbs
          crumbs={breadcrumbs}
        />
      </BreadcrumbsWrapper>
      <CompanyProductsWrapper profile={profileBySlug}>
        <ProductsContainer>
          <DynamicProducts
            brandIdsScope={brands.map(({ id }) => id)}
            marketId={marketId}
            route={productRoute}
            includeProfile
          />
        </ProductsContainer>
      </CompanyProductsWrapper>
    </>
  );
};

CompanyMarketProducts.propTypes = companyMarketProductsPropertyTypes;

const CompanyMarketProductsWithQuery = withQuery(CompanyMarketProducts);

const companyMarketProductsPagePropertyTypes = {
  slug: PropTypes.string.isRequired,
  configuration: configurationPropertyType,
};

type CompanyMarketProductsPageProperties = InferProps<
  typeof companyMarketProductsPagePropertyTypes
>;

const CompanyMarketProductsPage = ({
  slug,
  configuration,
}: CompanyMarketProductsPageProperties) => (
  <CompanyMarketProductsWithQuery
    query={profileBySlugQuery}
    variables={{
      slug,
      imageConfigurations: companyProfileImageConfigurations,
      mobileImageConfigurations: companyProfileMobileImageConfigurations,
      logoConfigurations: companyProfileLogoImageConfigurations,
      collectionImageConfigurations:
        companyProfileCollectionImageConfigurations,
    }}
    configuration={configuration}
    errorMessage="Error loading this company profile"
  />
);

CompanyMarketProductsPage.propTypes = companyMarketProductsPagePropertyTypes;

export type { MarketConfiguration };
export { CompanyMarketProducts };
export default CompanyMarketProductsPage;
